/** Store zustand de autenticación: usuario logueado, sucursal activa, normalización de empresas/sucursales, refresh de tokens, restauración de sesión y logout. */
import { create } from "zustand";
import { EmpresaUsuario, LoggedUserInnerwork, Sucursal } from "@/types";
import { clearStorage, getFromStorage, removeFromStorage, saveInStorage } from "@/utils";
import { useUIStore } from "@/stores/uiStore";
import { useAccionesPermitidasStore } from "@/stores/accionesPermitidasStore";

const ACTIVE_SUCURSAL_KEY = "innerwork-active-sucursal";
const WORKSPACE_RESUELTO_KEY = "innerwork-workspace-resuelto";

const normalizarSucursales = (sucursales: unknown): Sucursal[] | undefined => {
  if (!Array.isArray(sucursales)) return undefined;
  return (sucursales as Array<Record<string, unknown>>).map((raw, index) => {
    const src = raw ?? {};
    return {
      idSucursal: Number(src.idSucursal) || index + 1,
      nombre: String(src.nombreSucursal ?? src.nombre ?? ""),
      codigo: String(src.codSucursal ?? src.codigo ?? ""),
      cuit: String(src.cuit ?? ""),
      telefono: String(src.telefono ?? ""),
      email: String(src.email ?? ""),
      codEmpresa: String(src.codEmpresa ?? ""),
      nombreEmpresa: String(src.nombreEmpresa ?? ""),
    };
  });
};

/** Aplana `empresas[].sucursales[]` (nuevo shape del login) al listado plano de `Sucursal`, heredando el contexto de empresa del padre. */
const sucursalesDesdeEmpresas = (empresas: unknown): Sucursal[] | undefined => {
  if (!Array.isArray(empresas) || empresas.length === 0) return undefined;
  const planas: Array<Record<string, unknown>> = [];
  (empresas as EmpresaUsuario[]).forEach((empresa) => {
    const codEmpresa = String(empresa?.codEmpresa ?? "");
    const nombreEmpresa = String(empresa?.nombreEmpresa ?? "");
    (empresa?.sucursales ?? []).forEach((sucursal) => {
      planas.push({ ...sucursal, codEmpresa, nombreEmpresa });
    });
  });
  return normalizarSucursales(planas);
};

const normalizarUsuario = (user: LoggedUserInnerwork): LoggedUserInnerwork => {
  // `empresas` del login es autoritativo: de ahí re-derivamos el listado plano completo (todas las
  // empresas) para no perder sucursales de otras empresas al cambiar de workspace. Si no viniera,
  // caemos al listado plano persistido.
  const desdeEmpresas = sucursalesDesdeEmpresas(user.empresas);
  const desdePlano = user.sucursales?.length ? normalizarSucursales(user.sucursales) : undefined;
  const sucursales = (desdeEmpresas?.length ? desdeEmpresas : undefined) ?? desdePlano ?? [];

  // Cuando el login trae una sola empresa, se asume como contexto activo (evita el paso "Empresa").
  const empresaUnica = user.empresas?.length === 1 ? user.empresas[0] : undefined;

  return {
    ...user,
    codEmpresa: user.codEmpresa ?? empresaUnica?.codEmpresa,
    nombreEmpresa: user.nombreEmpresa ?? empresaUnica?.nombreEmpresa,
    sucursales,
  };
};

/** True cuando el usuario tiene más de una empresa: en ese caso siempre debe elegir empresa antes que sucursal. */
export const tieneMultiplesEmpresas = (user: LoggedUserInnerwork): boolean =>
  (user.empresas?.length ?? 0) > 1;

interface AuthState {
  loggedUserInnerwork: LoggedUserInnerwork;
  loadingApp: boolean;
  activeSucursal: Sucursal | null;
  /** Marca que el usuario ya resolvió su espacio de trabajo (eligió sucursal o decidió seguir sin una). */
  workspaceResuelto: boolean;
  setLoggedUserInnerwork: (user: LoggedUserInnerwork) => void;
  setActiveSucursal: (sucursal: Sucursal) => void;
  /** Deja el workspace a nivel empresa (sin sucursal): la data se consume mergeada para todas las sucursales. */
  setSinSucursal: () => void;
  /** Actualiza parcialmente el usuario logueado (p. ej. el contexto de empresa elegido en el wizard). */
  updateLoggedUser: (partial: Partial<LoggedUserInnerwork>) => void;
  refreshTokens: (accessToken: string, refreshToken: string) => void;
  restoreSession: () => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  loggedUserInnerwork: {},
  loadingApp: true,
  activeSucursal: null,
  workspaceResuelto: false,
  setLoggedUserInnerwork: (user) => {
    removeFromStorage("innerwork-menu");
    removeFromStorage("innerwork-modules");
    removeFromStorage("lastModule");
    const normalized = normalizarUsuario(user);
    saveInStorage("loggedUserInnerwork", normalized);

    const saved = get().activeSucursal;
    const sucursales = normalized.sucursales ?? [];
    const savedValida = !!saved && sucursales.some((s) => s.idSucursal === saved.idSucursal);
    // Con varias empresas siempre hay que elegir empresa primero, así que no se autoselecciona sucursal.
    const debeElegirEmpresa = tieneMultiplesEmpresas(normalized) && !normalized.codEmpresa;
    // Con una sola sucursal se autoselecciona; con varias se obliga a elegir (wizard de workspace).
    const unicaSucursal = sucursales.length === 1 && !debeElegirEmpresa ? sucursales[0] : null;
    const restore = !debeElegirEmpresa && savedValida ? saved : unicaSucursal;
    const workspaceResuelto = !!restore;
    if (restore) {
      saveInStorage(ACTIVE_SUCURSAL_KEY, restore);
      saveInStorage(WORKSPACE_RESUELTO_KEY, true);
    } else {
      removeFromStorage(ACTIVE_SUCURSAL_KEY);
      removeFromStorage(WORKSPACE_RESUELTO_KEY);
    }

    set({ loggedUserInnerwork: normalized, activeSucursal: restore, workspaceResuelto });
  },
  setActiveSucursal: (sucursal) => {
    saveInStorage(ACTIVE_SUCURSAL_KEY, sucursal);
    saveInStorage(WORKSPACE_RESUELTO_KEY, true);
    set({ activeSucursal: sucursal, workspaceResuelto: true });
  },
  setSinSucursal: () => {
    removeFromStorage(ACTIVE_SUCURSAL_KEY);
    saveInStorage(WORKSPACE_RESUELTO_KEY, true);
    set({ activeSucursal: null, workspaceResuelto: true });
  },
  updateLoggedUser: (partial) => {
    const updated = { ...get().loggedUserInnerwork, ...partial };
    saveInStorage("loggedUserInnerwork", updated);
    set({ loggedUserInnerwork: updated });
  },
  refreshTokens: (accessToken, refreshToken) => {
    const current = get().loggedUserInnerwork;
    const updated: LoggedUserInnerwork = {
      ...current,
      tokenDatos: {
        ...current.tokenDatos,
        accessToken,
        refreshToken,
      },
    };
    saveInStorage("loggedUserInnerwork", updated);
    set({ loggedUserInnerwork: updated });
  },
  restoreSession: async () => {
    try {
      const saved = await getFromStorage<LoggedUserInnerwork>("loggedUserInnerwork");
      const savedSucursal = await getFromStorage<Sucursal>(ACTIVE_SUCURSAL_KEY);
      const savedWorkspace = await getFromStorage<boolean>(WORKSPACE_RESUELTO_KEY);
      if (saved && Object.keys(saved).length > 0) {
        const user = normalizarUsuario(saved);
        let activeSucursal = savedSucursal ?? null;
        if (
          activeSucursal &&
          !(user.sucursales ?? []).some((s) => s.idSucursal === activeSucursal!.idSucursal)
        ) {
          // Sólo cae a la primera sucursal si hay una sola y no hay que elegir empresa; si no, se re-pide.
          activeSucursal =
            user.sucursales?.length === 1 && !tieneMultiplesEmpresas(user)
              ? user.sucursales[0]
              : null;
        }
        if (activeSucursal) saveInStorage(ACTIVE_SUCURSAL_KEY, activeSucursal);
        // Si ya eligió sucursal, o decidió seguir sin una, el workspace está resuelto.
        const workspaceResuelto = !!activeSucursal || savedWorkspace === true;
        set({ loggedUserInnerwork: user, activeSucursal, workspaceResuelto, loadingApp: false });
      } else {
        set({ loadingApp: false });
      }
    } catch {
      set({ loadingApp: false });
    }
  },
  logout: () => {
    clearStorage();
    removeFromStorage(ACTIVE_SUCURSAL_KEY);
    removeFromStorage(WORKSPACE_RESUELTO_KEY);
    useUIStore.getState().clearMenu();
    useAccionesPermitidasStore.getState().reset();
    set({ loggedUserInnerwork: {}, activeSucursal: null, workspaceResuelto: false });
  },
}));
